import { useState } from 'react';

import { useApi } from '../../lib/api/api-context.js';

const MAX_ICON_SIZE_BYTES = 1_048_576;
const ICON_ACCEPT = 'image/svg+xml';

interface AttachmentUploadResponse {
  readonly id: string;
  readonly url: string;
  readonly signed_url: string;
}

interface IconUploadProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled: boolean | undefined;
  readonly error: string | undefined;
}

export function IconUpload({
  value,
  onChange,
  disabled,
  error,
}: IconUploadProps): React.JSX.Element {
  const api = useApi();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const previewUrl = value.startsWith('http') || value.startsWith('/') ? value : '';

  const upload = async (file: File): Promise<void> => {
    if (file.type !== ICON_ACCEPT && !file.name.toLowerCase().endsWith('.svg')) {
      setUploadError('Please choose an SVG file.');
      return;
    }
    if (file.size > MAX_ICON_SIZE_BYTES) {
      setUploadError('Icons must be 1 MB or smaller.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await api.postForm<AttachmentUploadResponse>('/v1/attachments', form);
      onChange(response.signed_url);
    } catch (cause) {
      setUploadError(cause instanceof Error ? cause.message : 'The icon could not be uploaded.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      {previewUrl !== '' ? (
        <div className="relative aspect-square w-16 overflow-hidden rounded-md border border-border bg-muted p-2">
          <img className="h-full w-full object-contain" src={previewUrl} alt="" />
        </div>
      ) : null}
      <label className="flex min-h-24 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border text-center hover:border-primary">
        <span className="text-sm font-medium">
          {uploading ? 'Uploading icon…' : 'Drop an SVG icon here or click to browse'}
        </span>
        <span className="mt-1 text-xs text-muted-foreground">SVG only, up to 1 MB</span>
        <input
          className="sr-only"
          type="file"
          accept={ICON_ACCEPT}
          disabled={disabled ?? uploading}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) void upload(file);
            event.target.value = '';
          }}
        />
      </label>
      {uploadError !== null || error !== undefined ? (
        <p className="rounded bg-destructive/10 p-3 text-sm text-destructive" role="alert">
          {uploadError ?? error}
        </p>
      ) : null}
    </div>
  );
}
